import { IntakeHeightInput } from "../IntakeFormField";
import { useState } from "react";
import {
  cmToInches,
  HEIGHT_KEY,
  inchesToCm,
  kgToPounds,
  poundsToKg,
} from "./measurements";
import type { IntakeAnswer, IntakeQuestion } from "./types";

/** Units are explicit; the answer remains in the definition's canonical units. */
export function MeasurementInput({
  question,
  value,
  onChange,
  error,
}: {
  question: IntakeQuestion;
  value: IntakeAnswer | undefined;
  onChange: (value: number | null) => void;
  error: boolean;
}) {
  const [metric, setMetric] = useState(false);
  const height = question.key === HEIGHT_KEY;
  const unit = metric ? (height ? "cm" : "kg") : height ? "in" : "lb";
  const toDisplay = metric
    ? height
      ? inchesToCm
      : poundsToKg
    : (number: number) => number;
  const fromDisplay = metric
    ? height
      ? cmToInches
      : kgToPounds
    : (number: number) => number;
  return (
    <div className="flex gap-10">
      {height && !metric ? (
        <IntakeHeightInput
          value={typeof value === "number" ? String(value) : ""}
          onChange={(next) => onChange(next === "" ? null : Number(next))}
          label={question.label}
          invalid={error}
          required={question.required}
          describedBy={
            error ? `intake-question-${question.key}-error` : undefined
          }
        />
      ) : (
        <input
          aria-label={`${question.label} (${unit})`}
          aria-invalid={error}
          aria-required={question.required}
          aria-describedby={
            error ? `intake-question-${question.key}-error` : undefined
          }
          type="number"
          step="any"
          className="w-full min-w-0 h-50 px-20 bg-[#FAFAFA] border border-[#ECECEC] type-body-lg text-ink focus:outline-none"
          min={question.min == null ? undefined : toDisplay(question.min)}
          max={question.max == null ? undefined : toDisplay(question.max)}
          value={
            typeof value === "number" ? Number(toDisplay(value).toFixed(4)) : ""
          }
          onChange={(event) =>
            onChange(
              event.target.value === ""
                ? null
                : fromDisplay(Number(event.target.value)),
            )
          }
        />
      )}
      <select
        aria-label={`${question.label} units`}
        value={metric ? "metric" : "imperial"}
        className="h-50 border border-[#ECECEC] bg-[#FAFAFA] px-10 type-body-lg"
        onChange={(event) => setMetric(event.target.value === "metric")}
      >
        <option value="imperial">{height ? "ft / in" : "lb"}</option>
        <option value="metric">{height ? "cm" : "kg"}</option>
      </select>
    </div>
  );
}
